/**
 * Typed wrappers around the accounts/stats HTTP API (same origin, cookie auth).
 * Each returns `{ ok, data }` or `{ ok: false, error }` — never throws.
 */

import type {
  AccountInfo,
  CarConfig,
  CircuitStats,
  PlayerDirectoryEntry,
  ProfileData,
} from '@microligue/shared';

export type ApiResult<T> = { ok: true; data: T } | { ok: false; status: number; error: string };

async function request<T>(
  method: 'GET' | 'POST',
  url: string,
  body?: unknown,
): Promise<ApiResult<T>> {
  try {
    const res = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        error: typeof json.error === 'string' ? json.error : `Erreur ${res.status}.`,
      };
    }
    return { ok: true, data: json as T };
  } catch {
    return { ok: false, status: 0, error: 'Serveur injoignable.' };
  }
}

export const api = {
  me: () => request<{ me: AccountInfo }>('GET', '/api/me'),
  register: (pseudo: string, password: string, color: string) =>
    request<{ me: AccountInfo }>('POST', '/api/register', { pseudo, password, color }),
  login: (pseudo: string, password: string) =>
    request<{ me: AccountInfo }>('POST', '/api/login', { pseudo, password }),
  logout: () => request<{ ok: true }>('POST', '/api/logout'),
  setColor: (color: string) => request<{ me: AccountInfo }>('POST', '/api/color', { color }),
  setDesign: (design: CarConfig) =>
    request<{ me: AccountInfo }>('POST', '/api/me/design', { design }),
  promote: (pseudo: string) => request<{ ok: true }>('POST', '/api/admin/promote', { pseudo }),
  players: () => request<{ players: PlayerDirectoryEntry[] }>('GET', '/api/players'),
  profile: (pseudo: string) =>
    request<{ profile: ProfileData }>('GET', `/api/profile/${encodeURIComponent(pseudo)}`),
  circuitStats: (id: string) =>
    request<{ stats: CircuitStats }>('GET', `/api/circuit/${encodeURIComponent(id)}/stats`),
};
